import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ShieldCheck, Loader2, ImagePlus, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_LABEL = {
  instagram: 'Instagram', youtube: 'YouTube', tiktok: 'TikTok', facebook: 'Facebook', x: 'X / Twitter',
};

/**
 * VerifySocialButton — click to auto-scrape; if it fails, open manual upload dialog.
 * `status` is one of: undefined, "verified", "pending", "rejected".
 */
const VerifySocialButton = ({ platform, declared, status, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [screenshot, setScreenshot] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tryAutoVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/artists/me/verify-social', { platform });
      if (data.status === 'verified') {
        toast.success(`${PLATFORM_LABEL[platform]} verified`, { description: `Scraped ${data.scraped_count?.toLocaleString()} matches your declared count.` });
        onChange?.('verified');
      } else if (data.scraped_count) {
        toast.warning('Counts don\'t match', { description: `Found ${data.scraped_count.toLocaleString()} but you declared ${data.declared_count?.toLocaleString()}. Please upload a screenshot for manual review.` });
        setManualOpen(true);
        onChange?.('pending');
      } else {
        toast.info('Auto-scrape failed', { description: 'Many platforms block server checks. Please upload a screenshot for manual review.' });
        setManualOpen(true);
      }
    } catch (err) {
      toast.error('Verification failed', { description: err?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setScreenshot(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success('Screenshot uploaded — submit for review');
    } catch {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const submitManual = async () => {
    if (!screenshot) { toast.error('Please upload a screenshot first'); return; }
    setSubmitting(true);
    try {
      await api.post('/artists/me/social-proof', {
        platform, screenshot_url: screenshot, declared_count: Number(declared) || 0,
      });
      toast.success('Submitted for admin review', { description: 'Usually approved within 24 hours.' });
      onChange?.('pending');
      setManualOpen(false);
      setScreenshot('');
    } catch (err) {
      toast.error('Could not submit', { description: err?.response?.data?.detail });
    } finally { setSubmitting(false); }
  };

  if (status === 'verified') {
    return (
      <span data-testid={`social-verified-${platform}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-emerald-300 font-medium">
        <CheckCircle2 className="h-3 w-3" /> Verified
      </span>
    );
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        onClick={tryAutoVerify}
        disabled={busy}
        data-testid={`social-verify-button-${platform}`}
        className={`text-[11px] h-7 px-3 rounded-full border ${
          status === 'pending'
            ? 'bg-amber-500/15 border-amber-400/30 text-amber-200 hover:bg-amber-500/20'
            : 'bg-white/5 border-white/10 text-slate-200 hover:bg-white/10'
        }`}
      >
        {busy
          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> verifying…</>
          : status === 'pending'
          ? <><AlertTriangle className="h-3 w-3 mr-1" /> Pending review</>
          : <><ShieldCheck className="h-3 w-3 mr-1" /> Verify</>}
      </Button>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="social-manual-dialog" className="bg-[#0B1020] border-white/10 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-white">Verify your {PLATFORM_LABEL[platform]} count</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Please upload a clean screenshot of your {PLATFORM_LABEL[platform]} insights / profile page showing the follower count. Our admin team will review within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={screenshot} onChange={(e) => setScreenshot(e.target.value)} placeholder="Screenshot URL" data-testid="social-manual-screenshot-url" className="bg-white/5 border-white/10" />
            <label className="inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 cursor-pointer">
              <ImagePlus className="h-3.5 w-3.5" /> {uploading ? 'uploading…' : 'upload from device'}
              <input type="file" accept="image/*" hidden onChange={upload} disabled={uploading} />
            </label>
            {screenshot && (
              <img src={screenshot} alt="proof" className="rounded-lg border border-white/10 max-h-64 object-contain" />
            )}
            <Button type="button" onClick={submitManual} disabled={submitting} data-testid="social-manual-submit" className="btn-rainbow text-white border-0 w-full">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Submit for review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerifySocialButton;
